import React from 'react';

import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { endOfMonth, formatISO, isPast, isThisMonth } from 'date-fns';
import PropTypes from 'prop-types';

import { Text } from '@asteria/component-core/typography';

import { Input } from '@asteria/component-form';

import * as AppStore from '@asteria/datalayer/stores/app';

import { cn } from '@asteria/utils-funcs/classes';
import formatNumber from '@asteria/utils-funcs/formatNumber';

import {
	filterOperations,
	mergeOperations,
	updateOperations,
} from '../../../utils/operations';

import { useVisibleValue } from './hooks';

import '../styles.scss';

const XAxisInput = React.memo((props) => {
	const { type, date, category, tag } = props;
	const { getValues, setValue } = useFormContext();

	const [visibleValue, setVisibleValue] = React.useState(null);

	const adjustTag = useSelector((store) => {
		const tags = AppStore.selectors.tags(store);
		return tags.find((item) => item?.name === '$adjusted')?._id ?? null;
	});

	const tagId = useSelector((store) => {
		if (tag === '$deposit' || tag === '$withdraw') {
			return null;
		}

		const tags = AppStore.selectors.tags(store);
		const storedTag = tags.find((item) => item?.name === tag);

		return storedTag?._id ?? storedTag?.id ?? '$unknown';
	});

	const onChange = React.useCallback(
		({ value }) => {
			const allOperations = getValues('allOperations') ?? [];
			const operations = filterOperations(
				mergeOperations(allOperations),
				{
					type,
					tagId,
					startDate: formatISO(date, { representation: 'date' }),
					endDate: formatISO(endOfMonth(date), {
						representation: 'date',
					}),
				},
			);

			const changes = updateOperations({
				value,
				date,
				tagId,
				type,
				operations: operations,
				defaultTagId: adjustTag,
			});

			const existingChanges = getValues('changes') ?? [];
			setValue(`allOperations`, allOperations.concat(changes));
			setValue(`changes`, existingChanges.concat(changes));
		},
		[date, getValues, setValue, type, tagId, adjustTag],
	);

	const value = parseFloat(
		useVisibleValue({ type, date, category, tag }) ?? 0,
	).toString();
	const sign = type === 'deposit' ? '+' : '-';

	const inputFormat = React.useMemo(() => ({ valueAsNumber: true }), []);

	const updateVisibleValue = React.useCallback(() => {
		setVisibleValue(value);
	}, [value]);

	React.useEffect(() => {
		if (visibleValue === null) {
			updateVisibleValue();
		}
	}, [updateVisibleValue, visibleValue]);

	if (isPast(date) && !isThisMonth(date)) {
		return (
			<div
				className={cn(
					'asteria-component__forecaster-graph-input',
					'asteria--variant-history',
				)}
			>
				<Text align="center" size="sm">
					{value ? sign : null}
					{formatNumber(value, false, false, true)}
				</Text>
			</div>
		);
	}

	return (
		<Input
			className="asteria-component__forecaster-graph-input"
			uncontrolled
			format={inputFormat}
			type="number"
			onChange={onChange}
			min={0}
			value={value ?? visibleValue}
			onBlur={updateVisibleValue}
		/>
	);
});

XAxisInput.displayName = 'XAxisInput';
XAxisInput.propTypes = {
	type: PropTypes.string,
	date: PropTypes.string,
	category: PropTypes.string,
	tag: PropTypes.string,

	onAction: PropTypes.func,
};

export default XAxisInput;
